import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowRight,
  lucideLandmark,
  lucideMapPin,
  lucideWallet,
  lucideChartNoAxesColumn,
  lucideCreditCard,
  lucideStore,
  lucideTicket,
  lucideTicketCheck,
  lucideTriangleAlert,
  lucideUsers,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmAvatarImports } from '@spartan-ng/helm/avatar';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { KpiCardComponent } from '../../components/kpi-card/kpi-card.component';

import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Company, CompanyService } from '../../services/company.service';
import { LoggerService } from '../../services/logger.service';
import { SetupChecklistComponent } from '../../components/setup-checklist/setup-checklist.component';
import { SalesStatistics } from '../../services/statistics.service';
import {
  CompanyGroupService,
  CompanyGroup,
  MonthlyReport,
} from '../../services/company-group.service';
import { SetupProgressService } from '../../services/setup-progress.service';
import { formatCurrencyWithSymbol } from '../../utils/currency.utils';
import { berlinPeriod } from '../../utils/period.utils';
import { StorefrontService } from '../../services/storefront.service';
import { companyInitials } from '../../components/layout/navbar/company-initials';
import { environment } from '../../../environments/environment';
import {
  MEMBER_STATUS_LABELS,
  MemberStatus,
  memberSince,
  memberStatus,
} from '../../utils/member-status.utils';

/** How far along the company is in their journey. */
export type PageMode = 'onboarding' | 'setup' | 'ready' | 'active';

@Component({
  selector: 'app-landing-page',
  standalone: true,
  imports: [
    RouterLink,
    NgIcon,
    HlmAlertImports,
    HlmAvatarImports,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmItemImports,
    HlmSkeletonImports,
    HlmTypographyImports,
    KpiCardComponent,
    SetupChecklistComponent,
  ],
  providers: [
    provideIcons({
      lucideArrowRight,
      lucideLandmark,
      lucideMapPin,
      lucideWallet,
      lucideChartNoAxesColumn,
      lucideCreditCard,
      lucideStore,
      lucideTicket,
      lucideTicketCheck,
      lucideTriangleAlert,
      lucideUsers,
    }),
  ],
  templateUrl: './landing-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './landing-page.component.css',
})
export class LandingPageComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  /** Set when the statistics request finished without data (the service only logs the error). */
  private statisticsFailed = false;

  groups: CompanyGroup[] = [];
  /** The running month of the network this company runs, for the overview card. */
  networkMonth: MonthlyReport | null = null;
  private readonly setup = inject(SetupProgressService);

  constructor(
    private companyService: CompanyService,
    private groupService: CompanyGroupService,
    private storefrontService: StorefrontService,
    private logger: LoggerService,
  ) {}

  get centralStorefrontUrl(): string {
    const suburl = this.company?.group_company_suburl;
    if (!suburl) return '';
    const protocol = this.storefrontService.getProtocol();
    const domain = this.storefrontService.getStorefrontBaseDomain();
    return `${protocol}${suburl}.${domain}`;
  }

  // Read straight from the service signals: the old `company$` stream skipped failed
  // loads, which left the page on its skeleton forever.
  get company(): Company | null {
    return this.companyService.company();
  }

  get statistics(): SalesStatistics | null {
    return this.companyService.statistics();
  }

  get hasError(): boolean {
    return this.companyService.error() !== null || this.statisticsFailed;
  }

  get isLoading(): boolean {
    return !this.hasError && (!this.company || !this.statistics);
  }

  get pageMode(): PageMode {
    return this.company && this.statistics
      ? this.derivePageMode(this.company, this.statistics)
      : 'onboarding';
  }

  ngOnInit() {
    this.loadData();

    this.groupService
      .listGroups()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (groups) => {
          this.groups = groups;
          const own = this.ownNetwork;
          if (own) {
            this.groupService
              .getMonthlyReport(own.public_id, berlinPeriod(new Date()))
              .pipe(takeUntil(this.destroy$))
              .subscribe({ next: (report) => (this.networkMonth = report), error: () => {} });
          }
        },
        error: () => {
          /* non-critical, ignore */
        },
      });
  }

  retry(): void {
    this.loadData();
  }

  private loadData(): void {
    this.statisticsFailed = false;
    this.companyService.reloadCompanyData();
    this.companyService.loadInitialStatistics().then(() => {
      this.statisticsFailed = this.companyService.statistics() === null;
    });
  }

  private derivePageMode(company: Company, statistics: SalesStatistics): PageMode {
    if (!company.is_stripe_onboarding_success) {
      return 'onboarding';
    }
    // Same rule as the checklist: only the tasks that apply to this company count.
    if (this.setup.requiredPhase()) {
      return 'setup';
    }
    if (!statistics.total_vouchers_sold) {
      return 'ready';
    }
    return 'active';
  }

  /** One sentence describing where the company stands right now. */
  get intro(): string {
    if (this.company?.is_member) {
      const network = this.company.member_of_company_name || 'die Zentrale';
      if (this.company.group_membership_status === 'pending') {
        return `Du bist als Mitglied registriert. Sobald ${network} dich freigibt, kannst du Gutscheine des Netzwerks einlösen – wir schicken dir dann eine E-Mail.`;
      }
      if (this.company.group_membership_status !== 'active') {
        return `Du bist kein aktives Mitglied von ${network} mehr und kannst keine Gutscheine des Netzwerks einlösen.`;
      }
      return `Du bist Mitglied von ${network} und löst Gutscheine des Netzwerks ein. ${network} überweist dir deine Einlösungen einmal im Monat.`;
    }
    switch (this.pageMode) {
      case 'onboarding':
        return 'Schön, dass du dabei bist! Richte zuerst deine Zahlungsabwicklung ein, damit Kunden Gutscheine kaufen können – das dauert nur wenige Minuten.';
      case 'ready':
        return 'Dein Shop ist eingerichtet und bereit für die ersten Kunden! Viel Spaß und Erfolg wünscht dir das Schenkwerk-Team!';
      case 'active':
        return 'Hier siehst du auf einen Blick, wie dein Gutschein-Shop läuft.';
      default:
        return 'Noch ein paar Schritte, dann kannst du Gutscheine verkaufen.';
    }
  }

  get isFirstLogin(): boolean {
    if (!this.company?.first_login_at) return false;
    const firstLogin = new Date(this.company.first_login_at);
    return Date.now() - firstLogin.getTime() < 24 * 60 * 60 * 1000;
  }

  get greeting(): string {
    if (this.isFirstLogin) return `Willkommen, ${this.company?.name ?? ''}!`;
    return `Willkommen zurück, ${this.company?.name ?? ''}!`;
  }

  /** The network this company runs as its Zentrale. */
  get ownNetwork(): CompanyGroup | null {
    return this.groups.find((g) => g.central_company_id === this.company?.public_id) ?? null;
  }

  activeMemberCount(group: CompanyGroup): number {
    return group.members.filter((m) => m.status === 'active').length;
  }

  pendingMemberCount(group: CompanyGroup): number {
    return group.members.filter((m) => m.status === 'pending').length;
  }

  money(cents: number | null | undefined): string {
    return formatCurrencyWithSymbol(cents ?? 0);
  }

  /** The network this company is a member of - a member belongs to exactly one. */
  get network(): CompanyGroup | null {
    return this.groups.find((g) => g.central_company_id !== this.company?.public_id) ?? null;
  }

  get myStatus(): MemberStatus | null {
    return this.company?.is_member ? memberStatus(this.company) : null;
  }

  get myStatusLabel(): string {
    return this.myStatus ? MEMBER_STATUS_LABELS[this.myStatus] : '';
  }

  /** "seit März 2026", from the member's own entry in its network. */
  get mySince(): string | null {
    const mine = this.network?.members.find((m) => m.company_id === this.company?.public_id);
    return mine ? memberSince(mine.joined_at) : null;
  }

  initials(name: string | null): string {
    return companyInitials(name ?? undefined);
  }

  /** Logo of a company; the images endpoint already includes the uploads/ segment. */
  imageUrl(path: string | null): string | null {
    if (!path) return null;
    return `${environment.apiUrl}/images/${path.startsWith('uploads/') ? path.slice(8) : path}`;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
